<script setup async>
import {computed, ref, onMounted, onBeforeUnmount} from 'vue';

/**
 * MarketingBubbleHero — a tall (≈80vh) light hero. Behind the headline, the
 * sports we support drift around as floating, animated image bubbles, instantly
 * showing the breadth of head-to-head sport on offer. The bubbles also respond
 * to scroll (parallax + fade). Copy + CTAs sit centre stage. Deliberately NOT
 * the red brand hero — light, airy, energetic.
 *
 * Slots: #actions (CTA buttons), #note (small print under the CTAs).
 */
defineProps({
  eyebrow: {type: String, default: null},
  title: {type: String, required: true},
  subtitle: {type: String, default: null},
});

const {uploadFilenameToUrl} = useCrudConverters();
const sports = await useAllSports();

// Scatter ring around the centre — keeps the middle clear for the copy.
// top/left in %, size px, float dur+delay (s), parallax speed (px per scroll px),
// and whether it survives on phones (only the top/bottom band does, so bubbles
// never sit on top of the centred headline).
const slots = [
  // top band — safe on mobile
  {top: 7, left: 13, size: 66, dur: 9, delay: 0, speed: 0.12, mobile: true},
  {top: 6, left: 54, size: 54, dur: 10, delay: 1.2, speed: 0.2, mobile: true},
  {top: 12, left: 82, size: 60, dur: 8.5, delay: 0.5, speed: 0.16, mobile: true},
  // bottom band — safe on mobile
  {top: 85, left: 22, size: 62, dur: 10.5, delay: 1.6, speed: -0.18, mobile: true},
  {top: 88, left: 70, size: 56, dur: 9.5, delay: 0.8, speed: -0.24, mobile: true},
  // sides + fillers — desktop only
  {top: 34, left: 6, size: 78, dur: 9, delay: 0.3, speed: 0.26, mobile: false},
  {top: 58, left: 9, size: 58, dur: 11, delay: 2.1, speed: -0.14, mobile: false},
  {top: 30, left: 88, size: 64, dur: 8.8, delay: 1.4, speed: 0.22, mobile: false},
  {top: 60, left: 90, size: 72, dur: 10.5, delay: 0.9, speed: -0.2, mobile: false},
  {top: 16, left: 34, size: 50, dur: 9.2, delay: 2.4, speed: 0.1, mobile: false},
  {top: 13, left: 67, size: 56, dur: 10, delay: 0.6, speed: 0.18, mobile: false},
  {top: 80, left: 31, size: 60, dur: 8.6, delay: 1.9, speed: -0.16, mobile: false}, // clear of the centred footnote
];

// A sport links to its landing page only when the API provides a frontendLink.
const linkFor = (sport) => {
  const f = sport?.frontendLink;
  if (!f) return null;
  return f.startsWith('/') || f.startsWith('http') ? f : `/sports/${f}`;
};

const bubbles = computed(() => {
  const withImage = (sports ?? []).filter((s) => s.imageFileName);
  if (!withImage.length) return [];
  return slots.map((slot, i) => {
    const sport = withImage[i % withImage.length];
    return {
      key: `${sport.id}-${i}`,
      name: sportDisplayName(sport),
      url: uploadFilenameToUrl(sport.imageFileName, 'medium'),
      link: linkFor(sport),
      ...slot,
    };
  });
});

// ── Scroll response (parallax + fade) ──
const scrollY = ref(0);
let ticking = false;
let reduceMotion = false;

const onScroll = () => {
  if (ticking || reduceMotion) return;
  ticking = true;
  requestAnimationFrame(() => {
    scrollY.value = window.scrollY || 0;
    ticking = false;
  });
};

onMounted(() => {
  reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  if (reduceMotion) return;
  window.addEventListener('scroll', onScroll, {passive: true});
  onScroll();
});
onBeforeUnmount(() => window.removeEventListener('scroll', onScroll));

// Fade the whole field out across the first ~700px of scroll.
const fieldOpacity = computed(() => Math.max(0, 1 - scrollY.value / 700));

const bubbleTransform = (b) => `translate3d(0, ${Math.round(scrollY.value * b.speed)}px, 0)`;
</script>

<template>
  <section class="bh">
    <!-- floating sport bubbles -->
    <div class="bh-field" aria-hidden="true" :style="{ opacity: fieldOpacity }">
      <div
          v-for="b in bubbles"
          :key="b.key"
          class="bh-bubble"
          :class="{ 'bh-bubble--desk': !b.mobile }"
          :style="{
            top: b.top + '%',
            left: b.left + '%',
            width: b.size + 'px',
            height: b.size + 'px',
            transform: bubbleTransform(b),
          }"
      >
        <div class="bh-bubble-float" :style="{ animationDuration: b.dur + 's', animationDelay: b.delay + 's' }">
          <span class="bh-tip">{{ b.name }}</span>
          <component
              :is="b.link ? 'router-link' : 'div'"
              :to="b.link || undefined"
              :aria-label="b.link ? b.name : undefined"
              class="bh-bubble-pop"
              :class="{ 'bh-bubble-pop--link': b.link }"
          >
            <img :src="b.url" :alt="b.name" loading="lazy"/>
          </component>
        </div>
      </div>
    </div>

    <!-- copy -->
    <div class="bh-inner">
      <span v-if="eyebrow" class="bh-eyebrow">{{ eyebrow }}</span>
      <h1 class="bh-title">{{ title }}</h1>
      <p v-if="subtitle" class="bh-sub">{{ subtitle }}</p>
      <div class="bh-cta">
        <slot name="actions"/>
      </div>
      <div v-if="$slots.note" class="bh-note">
        <slot name="note"/>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.bh {
  position: relative;
  overflow: hidden;
  min-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 96px 20px 56px; /* clears the fixed app bar */
  background:
      radial-gradient(circle at 50% 18%, rgba(211, 41, 70, 0.07), transparent 52%),
      linear-gradient(180deg, #ffffff 0%, #f6f7f9 100%);
}

/* ── floating bubble field ── */
.bh-field {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  will-change: opacity;
}

/* outer: position + scroll parallax (JS-driven transform) */
.bh-bubble {
  position: absolute;
  will-change: transform;
}

/* mid: ambient float (kept separate so it never fights the parallax) */
.bh-bubble-float {
  position: relative;
  width: 100%;
  height: 100%;
  animation-name: bhFloat;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
  animation-direction: alternate;
}

/* inner: the visible crest — scales on hover (its own transform layer) */
.bh-bubble-pop {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  box-shadow: 0 18px 36px -18px rgba(26, 26, 26, 0.32);
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

/* linked crests are tappable everywhere (incl. touch) */
.bh-bubble-pop--link {
  pointer-events: auto;
  cursor: pointer;
}

@keyframes bhFloat {
  from { transform: translateY(8px); }
  to { transform: translateY(-18px); }
}

/* cool tooltip pill above the bubble (desktop hover) */
.bh-tip {
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%) translateY(5px);
  padding: 5px 11px;
  border-radius: 9px;
  background: rgb(var(--v-theme-text));
  color: rgb(var(--v-theme-surface));
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.2px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  box-shadow: 0 10px 22px -10px rgba(26, 26, 26, 0.55);
  transition: opacity 0.2s ease, transform 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: rgb(var(--v-theme-text));
  }
}

/* hover-capable pointers only — touch devices skip the interaction entirely */
@media (hover: hover) {
  .bh-bubble {
    pointer-events: none;
  }

  .bh-bubble-pop {
    pointer-events: auto;
  }

  .bh-bubble:hover {
    z-index: 5;
  }

  .bh-bubble:hover .bh-bubble-pop {
    transform: scale(1.16);
    box-shadow: 0 24px 46px -16px rgba(26, 26, 26, 0.5);
    border-color: rgb(var(--v-theme-primary));
  }

  .bh-bubble:hover .bh-tip {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

/* ── copy ── */
.bh-inner {
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 760px;
}

.bh-eyebrow {
  display: inline-block;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1.6px;
  color: rgb(var(--v-theme-primary));
  padding: 6px 14px;
  border-radius: 999px;
  background: rgb(var(--v-theme-brandTint));
  margin-bottom: 18px;
}

.bh-title {
  font-size: 34px;
  font-weight: 900;
  line-height: 1.05;
  letter-spacing: -1.2px;
  color: rgb(var(--v-theme-text));
  text-wrap: balance;
}

.bh-sub {
  font-size: 15px;
  line-height: 1.6;
  color: rgb(var(--v-theme-charcoal));
  margin: 16px auto 0;
  max-width: 520px;
}

.bh-cta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-top: 28px;
}

.bh-note {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 16px;
}

@media (min-width: 600px) {
  .bh-title { font-size: 52px; }
  .bh-sub { font-size: 16px; }
}

@media (min-width: 960px) {
  .bh-title { font-size: 64px; letter-spacing: -1.4px; }
  .bh-sub { font-size: 18px; }
}

/* phones: hide the side/filler bubbles, keep the top + bottom band clear of copy */
@media (max-width: 700px) {
  .bh {
    min-height: 86vh;
    padding-top: 84px;
  }

  .bh-bubble--desk { display: none; }
}

@media (prefers-reduced-motion: reduce) {
  .bh-bubble-float {
    animation: none;
  }
}
</style>
